import { nombrePais } from '@domino/schema'
import { useCallback } from 'react'
import { Bandera } from '../componentes/Bandera.js'
import { Cargando, Problema, Vacio } from '../componentes/Estados.js'
import { Icono } from '../componentes/Icono.js'
import { useCarga } from '../carga.js'
import { obtenerRepositorio } from '../datos/index.js'
import type { Jugador, ResumenCarrera } from '../datos/index.js'
import { conSigno, fechaCorta, puesto, signoDe } from '../formato.js'
import { ir } from '../navegacion.js'

/**
 * El perfil: quién es, y todo lo que jugó, sumado.
 *
 * Arriba su ficha, con los datos que tiene el club (es lo que confirma con qué
 * cuenta está mirando, y dónde ve si su número o su club están mal). Debajo,
 * lo que antes era el historial: sus números y los torneos que terminó, cada
 * uno con su detalle. La lista repite los jugados de «Torneos» a propósito:
 * allá se busca un torneo, acá se mira la carrera.
 *
 * Suma solo torneos terminados. El que está en curso tiene su propia pantalla y
 * meterlo acá haría que el total cambiara cada vez que se cierra una ronda, lo
 * que convierte un historial en un marcador en vivo — que es justo lo que no
 * es.
 *
 * El porcentaje de victorias va arriba y no la cantidad de torneos porque es el
 * número que se puede comparar entre jugadores con distinta cantidad de años
 * encima. Los totales crudos quedan al lado para el que quiere la cuenta.
 */
export function Perfil({ jugador, alSalir }: { jugador: Jugador; alSalir: () => void }) {
  const pedir = useCallback(async () => {
    const repo = await obtenerRepositorio()
    return repo.resumenDeCarrera()
  }, [])

  const { datos, error, cargando, recargar } = useCarga(pedir)

  if (error) return <Problema mensaje={error} alReintentar={recargar} />

  return (
    <div className="pantalla">
      <Ficha jugador={jugador} />

      {!datos ? (
        cargando && <Cargando que="Sumando tu historial…" />
      ) : datos.torneosJugados === 0 ? (
        <Vacio titulo="Todavía no terminaste ningún torneo">
          <p>Cuando termine el primero, su resultado y el acumulado de tus partidas quedan acá.</p>
        </Vacio>
      ) : (
        <>
          <section className="seccion">
            <h3 className="seccion-titulo">Tus números</h3>
            <Carrera resumen={datos} />
          </section>
          <Terminados resumen={datos} />
        </>
      )}

      <button className="boton secundario perfil-salir" onClick={alSalir}>
        <Icono nombre="salir" tamano={17} />
        Cerrar sesión
      </button>
    </div>
  )
}

/** Su ficha: el nombre en grande y los datos que el club tiene de él. */
function Ficha({ jugador }: { jugador: Jugador }) {
  const pais = jugador.pais ? nombrePais(jugador.pais) : null
  const iniciales = jugador.nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('')
  const debajo = [
    jugador.apodo && `«${jugador.apodo}»`,
    jugador.numero !== null && `N.º ${jugador.numero}`,
  ].filter(Boolean)

  return (
    <>
      <header className="perfil-cabecera">
        <span className="perfil-iniciales" aria-hidden="true">
          {iniciales}
        </span>
        <div>
          <h1>{jugador.nombre}</h1>
          {debajo.length > 0 && <p className="tenue">{debajo.join(' · ')}</p>}
        </div>
      </header>

      <dl className="resumen-datos">
        <div>
          <dt>Club</dt>
          <dd>{jugador.organizacion}</dd>
        </div>
        <div>
          <dt>País</dt>
          <dd>
            {pais ? (
              <>
                <Bandera codigo={jugador.pais} /> {pais}
              </>
            ) : (
              <span className="tenue">Sin cargar</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Usuario</dt>
          <dd>{jugador.usuario}</dd>
        </div>
        <div>
          <dt>Correo</dt>
          <dd className="acortar">
            {jugador.email || <span className="tenue">Sin cargar</span>}
          </dd>
        </div>
      </dl>
    </>
  )
}

/** Los torneos que terminó, del más nuevo al más viejo, con su detalle. */
function Terminados({ resumen }: { resumen: ResumenCarrera }) {
  return (
    <section className="seccion">
      <h3 className="seccion-titulo">Torneos terminados</h3>
      <ul className="lista-torneos">
        {resumen.torneos.map((t) => {
          const podio = t.posicion <= 3
          return (
            <li key={t.id}>
              <button onClick={() => ir({ nombre: 'torneo', id: t.id, pestana: 'posiciones' })}>
                <span className={`historial-puesto ${podio ? 'podio' : ''}`}>
                  {podio && <Icono nombre="medalla" tamano={18} />}
                  <b>{puesto(t.posicion)}</b>
                  <span className="tenue">de {t.participantes}</span>
                </span>
                <span className="lista-principal">
                  <b>{t.nombre}</b>
                  <span className="tenue">
                    {fechaCorta(t.fecha)}
                    {t.sede && ` · ${t.sede}`}
                  </span>
                  <span className="lista-datos">
                    <span className="ganadas">{t.ganadas}G</span>
                    <span className="perdidas">{t.perdidas}P</span>
                    <span className={`efectividad ${signoDe(t.efectividad)}`}>
                      {conSigno(t.efectividad)}
                    </span>
                    <span className="tenue">{t.puntosFavor} pts</span>
                  </span>
                </span>
                <Icono nombre="flecha" tamano={18} />
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function Carrera({ resumen }: { resumen: ResumenCarrera }) {
  const partidas = resumen.partidasGanadas + resumen.partidasPerdidas
  const porcentaje = partidas === 0 ? 0 : Math.round((resumen.partidasGanadas / partidas) * 100)

  return (
    <section className="carrera">
      <div className="carrera-principal">
        <span className="valor">{porcentaje}%</span>
        <span className="etiqueta">
          de {partidas} partidas ganadas
          <br />
          <span className="tenue">
            {resumen.partidasGanadas} ganadas · {resumen.partidasPerdidas} perdidas
          </span>
        </span>
      </div>

      <dl className="carrera-datos">
        <div>
          <dt>Torneos</dt>
          <dd>{resumen.torneosJugados}</dd>
        </div>
        <div>
          <dt>Mejor puesto</dt>
          <dd>{resumen.mejorPuesto === null ? '-' : puesto(resumen.mejorPuesto)}</dd>
        </div>
        <div>
          <dt>Podios</dt>
          <dd>{resumen.podios}</dd>
        </div>
        <div>
          <dt>Efectividad</dt>
          <dd className={`efectividad ${signoDe(resumen.efectividad)}`}>
            {conSigno(resumen.efectividad)}
          </dd>
        </div>
      </dl>
    </section>
  )
}
